import { lazy } from 'react';
import { Routes } from 'react-router-dom';
import { ProtectedRouteProps } from './auth/ProtectedRoute';

// Lazy-load every route component so the initial bundle is just the shell +
// login. Heavy pages (AddCocktail, MenuDetail, Profile) only load when their
// route is hit. <Suspense> in index.tsx covers the in-flight chunk fetch.
const AdminPanel = lazy(() => import('./pages/admin/AdminPanel'));
const Profile = lazy(() => import('./pages/profile/Profile').then(m => ({ default: m.Profile })));
const MenuList = lazy(() => import('./pages/menus/MenuList'));
const MenuDetail = lazy(() => import('./pages/menus/MenuDetail'));
const AddMenu = lazy(() => import('./pages/menus/AddMenu'));
const IngredientList = lazy(() => import('./pages/liquor/IngredientList'));
const IngredientDetail = lazy(() => import('./pages/liquor/IngredientDetail'));
const AddCocktail = lazy(() => import('./pages/cocktails/add-cocktail/AddCocktail'));
const AddIngredient = lazy(() => import('./pages/liquor/AddIngredient'));
const ConfirmDeleteIngredient = lazy(() => import('./pages/liquor/ConfirmDeleteIngredient'));
const CocktailDetail = lazy(() => import('./pages/cocktails/CocktailDetail'));
const CocktailList = lazy(() => import('./pages/cocktails/CocktailList'));
const Dashboard = lazy(() => import('./pages/dashboard/Dashboard'));
const Cellar = lazy(() => import('./pages/cellar/Cellar'));
const WinePage = lazy(() => import('./pages/wine/Wine'));
const WineDetail = lazy(() => import('./pages/wine/WineDetail'));
const Spirits = lazy(() => import('./pages/spirits/Spirits'));
const SpiritDetail = lazy(() => import('./pages/spirits/SpiritDetail'));

export enum RouteName {
    cocktailList = 1,
    ingredientList,
    ingredientDetail,
    ingredientEdit,
    deleteIngredient,
    createIngredient,
    adminHome,
    createCocktail,
    editCocktail,
    cocktailDetail,
    profileHome,
    menu,
    menuDetail,
    menuEdit,
    createMenu,
    publicMenu,
    dashboard,
    cellar,
    wine,
    wineDetail,
    spirits,
    spiritDetail,
}

export const SORTED_ROUTES: ProtectedRouteProps[] = [
    {
        name: RouteName.ingredientList,
        path: "/ingredients",
        children: <IngredientList />,
        roles: ['admin', 'standard']
    },
    {
        name: RouteName.createIngredient,
        path: "/ingredient/create",
        children: <AddIngredient/>,
        roles: ['admin', 'standard']
    },
    {
        name: RouteName.ingredientDetail,
        path: "/ingredient/:id",
        children: <IngredientDetail/>,
        roles: ['admin', 'standard']
    },
    {
        name: RouteName.ingredientEdit,
        path: "/ingredient/edit/:id",
        children: <AddIngredient/>,
        roles: ['admin', 'standard']
    },
    {
        name: RouteName.deleteIngredient,
        path: "/ingredient/delete/:id",
        children: <ConfirmDeleteIngredient/>,
        roles: ['admin', 'standard']
    },
    {
        name: RouteName.adminHome,
        path: "/admin/*",
        children: <AdminPanel/>,
        roles: ['admin']
    },
    {
        name: RouteName.createCocktail,
        path: "/cocktail/create",
        children: <AddCocktail/>,
        roles: ['admin', 'standard']
    },
    {
        name: RouteName.editCocktail,
        path: "/cocktail/edit/:id",
        children: <AddCocktail/>,
         roles: ['admin', 'standard']
    },
    {
        name: RouteName.cocktailDetail,
        path: "/cocktail/:id",
        children: <CocktailDetail/>,
        roles: ['admin', 'standard']
    },
    {
        name: RouteName.createMenu,
        path: "/menu/create",
        children: <AddMenu />,
        roles: ['admin', 'standard']
    },
    {
        name: RouteName.menuEdit,
        path: "/menu/edit/:id",
        children: <AddMenu />,
        roles: ['admin', 'standard']
    },
    {
        name: RouteName.menuDetail,
        path: "/menu/:id",
        children: <MenuDetail />,
        roles: ['admin', 'standard']
    },
    {
        name: RouteName.menu,
        path: "/menu",
        children: <MenuList />,
        roles: ['admin', 'standard']
    },
    {
        name: RouteName.profileHome,
        path: "/profile",
        children: <Profile/>,
        roles: ['admin', 'standard']
    },
    {
        name: RouteName.cocktailList,
        path: '/cocktails/*',
        children: <CocktailList/>,
        roles: ['admin', 'standard'],
        isPublic: true,
    },
    {
        name: RouteName.cellar,
        path: '/cellar',
        children: <Cellar/>,
        roles: ['admin', 'standard']
    },
    {
        name: RouteName.wine,
        path: '/wine/*',
        children: <WinePage/>,
        roles: ['admin', 'standard'],
        isPublic: true,
    },
    {
        name: RouteName.wineDetail,
        path: '/wine/detail/:id',
        children: <WineDetail/>,
        roles: ['admin', 'standard'],
        isPublic: true,
    },
    {
        name: RouteName.spirits,
        path: '/spirits/*',
        children: <Spirits/>,
        roles: ['admin', 'standard'],
        isPublic: true,
    },
    {
        name: RouteName.spiritDetail,
        path: '/spirit/:id',
        children: <SpiritDetail/>,
        roles: ['admin', 'standard'],
        isPublic: true,
    },
    {
        name: RouteName.dashboard,
        path: '/',
        children: <Dashboard/>,
        roles: ['admin', 'standard']
    }
]

export const ROUTES: Record<RouteName, ProtectedRouteProps> = SORTED_ROUTES.reduce(
  (acc, route) => {
    if (route.name) {
        acc[route.name] = route;
    }
    return acc;
  },
  {} as Record<RouteName, ProtectedRouteProps>
);
